import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, EffectFade } from "swiper/modules";

import { Box, Flex, Text, Button, Icon } from "@chakra-ui/react";
import "swiper/css";
import "swiper/css/effect-fade";

// Import react-spring for animations
import { useTransition, animated } from "@react-spring/web";

const Lightbox = (data) => {
  const swiperRef = useRef(null);
  const location = useLocation();
  const [arrowShowed, setArrowShowed] = useState(false);
  const [pathData, setPathData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const progressBarRef = useRef(null);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const transitions = useTransition(data, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 1 },
    config: { duration: 1500 },
  });

  // Determine if the device is a tablet based on window width
  const isTablet = windowWidth <= 850;

  const onAutoplayTimeLeft = (s, time, progress) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${(1 - progress) * 100}%`;
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const eventoParam = queryParams.get("event");

  useEffect(() => {
    if (eventoParam) {
      import(`../../assets/images/${eventoParam}/path.json`)
        .then((module) => {
          setPathData(module.default);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error loading JSON data:", error);
          setIsLoading(false);
        });
    }
  }, [eventoParam]);

  if (isLoading) {
    return <Text>Loading...</Text>;
  }

  const events = [
    { evento: "bocelli" },
    { evento: "castagneto" },
    { evento: "cerruti" },
    { evento: "ferragamo" },
    { evento: "gucci" },
    { evento: "pergola" },
    { evento: "siena" },
    { evento: "vaticano" },
    { evento: "palazzo vecchio" },
  ];

  const showArrows = () => {
    setArrowShowed(true);
  };

  const hideArrows = () => {
    setArrowShowed(false);
  };

  const handleLink = (str) => {
    // Se il la stringa evento contiene più di una parola
    // vengono ritornate tutte con la prima lettera maiuscola
    if (str.indexOf(" ") !== -1) {
      let newArr = str.split(" ");

      let words = newArr.map((i, index) => {
        let word;
        if (index === 0) {
          let upChar = i.charAt(0).toUpperCase();
          word = upChar + i.slice(1);
        } else {
          word = `${i.charAt(0).toUpperCase()}.`;
        }

        return word;
      });

      return words.join(" ");
    } else {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  };

  // Determina l'indice della slide iniziale
  const initialSlideIndex = events.findIndex(
    (e) => e.evento.toLowerCase() === eventoParam?.toLowerCase()
  );

  return transitions((style) => (
    <animated.section style={style}>
      <Box mt={{ base: "2em", xl: "0" }} minH={"100vh"}>
        <Flex
          justify={"center"}
          onMouseEnter={showArrows}
          onMouseLeave={hideArrows}
        >
          <Box
            textAlign={"center"}
            w={{ base: "80%", xl: "35%" }}
            my={{ base: "2em", xl: "2em" }}
            position="relative"
          >
            <Button
              display={{ base: "inline", xl: arrowShowed ? "inline" : "none" }}
              variant={"ghost"}
              background={"none"}
              _hover={{
                background: "none",
              }}
              position="absolute"
              left="-50px"
              top="50%"
              transform="translateY(-50%)"
              zIndex="10"
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <Icon viewBox="0 0 24 24" fill="ambra" width="24px" height="24px">
                <path d="M16.707 4.707 9.414 12l7.293 7.293-1.414 1.414L6.586 12l8.707-8.707 1.414 1.414z" />
              </Icon>
            </Button>
            <Swiper
              ref={swiperRef}
              modules={[Navigation]}
              loop={true}
              // pagination={{
              //   clickable: true,
              // }}
              slidesPerView={3}
              centeredSlides={true}
              spaceBetween={40}
              style={{ padding: "0" }}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              initialSlide={initialSlideIndex !== -1 ? initialSlideIndex : 0}
            >
              {events.map((e, index) => (
                <SwiperSlide key={index}>
                  <a href={`/lightbox?event=${encodeURIComponent(e.evento)}`}>
                    <Button
                      fontWeight={eventoParam === e.evento && 800}
                      color={eventoParam === e.evento ? "ambra" : "#fff"}
                      background={"none"}
                      variant={"ghost"}
                      border={"none"}
                      _hover={{
                        background: "none",
                        color: "ambra",
                      }}
                      p={0}
                    >
                      {handleLink(e.evento)}
                    </Button>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
            <Button
              display={{ base: "inline", xl: arrowShowed ? "inline" : "none" }}
              variant={"ghost"}
              background={"none"}
              _hover={{
                background: "none",
              }}
              position="absolute"
              right="-50px"
              top="50%"
              transform="translateY(-50%)"
              zIndex="10"
              onClick={() => swiperRef.current?.slideNext()}
            >
              <Icon viewBox="0 0 24 24" fill="ambra" width="24px" height="24px">
                <path d="M7.293 4.707L14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z" />
              </Icon>
            </Button>
          </Box>
        </Flex>

        {eventoParam && pathData.length ? (
          <Box>
            {isTablet ? (
              <Box>
                {pathData.map((item, index) => (
                  <Flex
                    key={index}
                    as={"figure"}
                    background={"#1A202C"}
                    justify={"center"}
                    alignContent={"center"}
                    mb={"3em"}
                  >
                    <img
                      src={require(`../../assets/images/${eventoParam}/${item.imageFileName}`)}
                      loading="lazy"
                      alt={item.altImage}
                    />
                  </Flex>
                ))}
              </Box>
            ) : (
              <Box>
                <Box className="autoplay-progress">
                  <Box
                    ref={progressBarRef}
                    h={"0.5em"}
                    bg={"ambra"}
                    w={0}
                    transition={"width 0.1s linear"}
                  ></Box>
                </Box>
                <Swiper
                  style={{
                    padding: 0,
                    marginBottom: "3em",
                  }}
                  effect="fade"
                  coverflowEffect={{
                    rotate: 0,
                    stretch: 0,
                    depth: 150,
                    modifier: 2.5,
                    slideShadows: true,
                  }}
                  autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                  }}
                  onAutoplayTimeLeft={onAutoplayTimeLeft}
                  modules={[Autoplay, EffectFade]}
                >
                  {pathData.map((item, index) => (
                    <SwiperSlide key={index}>
                      <Flex
                        as={"figure"}
                        background={"#1A202C"}
                        justify={"center"}
                        alignContent={"center"}
                        mb={0}
                        height={{ xl: "100vh" }}
                      >
                        <img
                          src={require(`../../assets/images/${eventoParam}/${item.imageFileName}`)}
                          loading="lazy"
                          alt={item.altImage}
                        />
                      </Flex>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            )}
          </Box>
        ) : (
          <Text>No image found</Text>
        )}
      </Box>
    </animated.section>
  ));
};

export default Lightbox;
